import { useCallback, useMemo } from 'react';

import { Combobox, ComboboxData, ComboboxItem, InputBase, useCombobox } from 'ui';

import { SearchableSelectWithComboBox } from '../types';
import { filterSearchableData } from '../utils/filterSearchableData';
import { flattenSearchData } from '../utils/flattenSearchData';
import { getKeyFromData } from '../utils/getKeyFromData';
import { SearchableOption } from './SearchableOption';

type CreatableSearchableSelectProps = Omit<
  SearchableSelectWithComboBox,
  'useFetchSource' | 'initialData' | 'onSearchChange'
> & {
  rightSection: React.ReactNode;
  isLoading: boolean;
  addCreatedOption: (searchValue: string, saveToDataRef: boolean) => ComboboxItem;
  onSearchChange: (searchValue?: string) => void;
};

const CreatableSearchableSelect = ({
  value,
  onChange,
  onSearchChange,
  data = [],
  searchValue,
  isLoading,
  createOption,
  rightSection,
  placeholder,
  inputProps,
  error,
  label,
  addCreatedOption,
  saveCreatedOptions,
  disabled,
  readOnly,
  nothingFoundMessage,
  ...props
}: CreatableSearchableSelectProps) => {
  const getSelectedOption = useCallback(
    (value: string) => {
      const filteredData = flattenSearchData(data);
      const selectedOption = filteredData.find((item) => {
        if (typeof item === 'string') {
          return item === value;
        }
        return item.value === value;
      });
      return typeof selectedOption === 'string'
        ? { value: selectedOption, label: selectedOption }
        : selectedOption;
    },
    [data]
  );

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      onSearchChange?.(undefined);
    },
    onDropdownOpen: (eventSource) => {
      if (eventSource === 'keyboard') {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex('active');
      }
    },
  });

  /** Returns the search value, if the search value is not set, then return the value */
  const search = useMemo(() => {
    if (searchValue !== undefined) return searchValue;
    if (value) {
      const selectedOption = getSelectedOption(value);
      if (!selectedOption) {
        return value;
      }
      return selectedOption?.label ?? selectedOption?.value;
    } else {
      return '';
    }
  }, [getSelectedOption, searchValue, value]);

  const filteredData = useMemo(() => {
    if (!searchValue) return data;
    const filtered = filterSearchableData(data, searchValue);

    return filtered;
  }, [data, searchValue]);

  const options = useMemo(
    () =>
      filteredData.map((item: ComboboxData[number], index: number) => (
        <SearchableOption
          item={item}
          activeOptionId={getSelectedOption(value ?? '')?.value}
          // Derive the key from the item
          key={getKeyFromData(item) ?? index}
        />
      )),
    [filteredData, getSelectedOption, value]
  );

  /**
   * Renders the create option if available
   */
  const renderCreateOption = useMemo(() => {
    if (isLoading) return null;
    if (filteredData.length > 0) return null;
    if (!createOption) return null;
    const createOptionComponent =
      typeof createOption === 'function'
        ? createOption({ searchValue: searchValue ?? '' })
        : ' + Create ' + searchValue;

    if (!createOptionComponent) return null;
    return (
      <Combobox.Option value="$create" data-testid="creatable-searchable-select-create-option">
        {createOptionComponent}
      </Combobox.Option>
    );
  }, [createOption, filteredData.length, isLoading, searchValue]);

  const nothingFoundMessageComponent = useMemo(() => {
    if (options.length > 0) return null;
    if (isLoading) return null;
    if (!nothingFoundMessage) return null;
    if (renderCreateOption === null) {
      return <Combobox.Empty>{nothingFoundMessage}</Combobox.Empty>;
    }
    return null;
  }, [isLoading, nothingFoundMessage, options.length, renderCreateOption]);

  return (
    <Combobox
      {...props}
      store={combobox}
      onOptionSubmit={(val) => {
        if (val === '$create' && createOption) {
          const newOption = addCreatedOption(searchValue ?? '', Boolean(saveCreatedOptions));
          onChange?.(newOption.value ?? '', newOption);
        } else {
          const selectedOption = getSelectedOption(val);

          onChange?.(selectedOption?.value ?? '', selectedOption);
          onSearchChange?.(selectedOption?.label ?? '');
        }
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          value={search ?? ''}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            onSearchChange?.(event.currentTarget.value);
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
          }}
          placeholder={placeholder}
          rightSection={rightSection}
          error={error}
          label={label}
          radius="sm"
          data-testid="creatable-searchable-select-input"
          disabled={disabled}
          readOnly={readOnly}
          {...inputProps}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {options}
          {renderCreateOption}
          {nothingFoundMessageComponent}
        </Combobox.Options>
      </Combobox.Dropdown>
      <Combobox.HiddenInput
        value={value ?? ''}
        data-testid="creatable-searchable-select-value-input"
      />
    </Combobox>
  );
};

export { CreatableSearchableSelect };
